import React from 'react'

import { AspectRatio, Box, Stack, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { Disclaimer } from 'components/atoms/disclaimer'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { useIsMobile } from 'hooks/useIsMobile'

import useStyles from './index.styles'
import { HeroBannerProps } from '..'
import { BannerItem } from '../bannerItem'
import { MedicalFacts } from '../medicalFacts'
import { Navigator } from '../navigator'
import { BlockItem } from '../navigator/blockItem'
import { FullBg } from '../navigator/blockItem/fullBg'

const CtaBanner = ({
  colorArr,
  sessionProps: { imageBanner, navigating: navigatingInit, medicalFacts },
  styles,
  navigatorProps,
  bannerTracking,
  bannerComponent,
  listBanner = [],
  autoSlide,
  delayTime,
  mappingIconList,
}: HeroBannerProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const navigating = [...navigatingInit].slice(0, 4) // for test layout
  const currentUrl = useCurrentUrl()
  const { classes } = useStyles(
    { numberItem: navigating.length },
    {
      name: 'CarePathHeroBannerCTALayout',
      styles,
    },
  )
  const listUrl = useHandlerUrls([
    imageBanner[0].linkTo,
    ...navigating.map((i) => i.url),
  ])

  const Banner = bannerComponent || BannerItem
  return (
    <Box className={classes.root}>
      <Container>
        <Stack
          sx={(theme) => ({
            gap: rem(16),
            paddingBottom: medicalFacts.listContent.length === 0 ? rem(24) : 0,
            [theme.fn.largerThan('sm')]: {
              gap: rem(40),
              paddingBottom:
                medicalFacts.listContent.length === 0 ? rem(40) : 0,
            },
          })}
        >
          <Box className={classes.row}>
            <Box className={classes.column}>
              <AspectRatio ratio={isMobile ? 343 / 347 : 552 / 414}>
                <Box>
                  <Banner
                    {...imageBanner[0]}
                    listBanner={listBanner}
                    dataTracking={bannerTracking}
                    classNames={{
                      root: classes.mainBannerWrapper,
                      ctaBtn: classes.mainBannerCtaBtn,
                      ctaBtnRightIcon: classes.mainBannerCtaBtnRightIcon,
                    }}
                    ratio={isMobile ? 343 / 347 : 552 / 414}
                    primaryColor={colorArr[0]}
                    autoSlide={autoSlide}
                    delayTime={delayTime}
                  />
                </Box>
              </AspectRatio>
            </Box>
            <Box className={classes.column}>
              <Navigator
                className={classes.navigatorRoot}
                colorArr={colorArr}
                navigating={navigating}
                itemComponent={FullBg}
                mappingIconList={mappingIconList}
                {...navigatorProps}
              >
                {navigating.map((i, idx) => (
                  <BlockItem
                    data-index={idx}
                    key={String(idx)}
                    classNames={{
                      root: classes.navigatorItem,
                      wrapper: classes.navigatorItemWrapper,
                      desc: classes.navigatorItemDesc,
                    }}
                    isQuare={
                      (!isMobile && navigating.length === 1) ||
                      (isMobile && navigating.length % 2 === 0) ||
                      navigating.length === 4 ||
                      (navigating.length === 3 && idx !== 2)
                    }
                    hideDescription={
                      navigating.length === 4 ||
                      (navigating.length === 3 && (idx + 1) % 3 !== 0)
                    }
                    isImageBg={i.isImageBg}
                    // {...(i.sessionLayout ===
                    //   SessionType.EMBEDDING_RISKSCREENER && {
                    //   onClick: onClickRiskScreener,
                    // })}
                    data-event-label={
                      i.url ||
                      `${currentUrl.split('#')[0]}${
                        i.idScroll ? '#' + decodeURI(i.idScroll) : ''
                      }`
                    }
                    isExternalUrl={listUrl[idx + 1]?.isExternalSite}
                    target={
                      listUrl[idx + 1]?.isExternalUrl ? '_blank' : '_self'
                    }
                  />
                ))}
              </Navigator>
            </Box>
          </Box>
          {isMobile && listUrl.filter((i) => i.isExternalSite).length > 0 && (
            <Disclaimer
              classNames={{
                root: classes.disclaimer,
                title: classes.disclaimerTitle,
              }}
              title={f({
                id: 'confirmLeavePage.content',
                defaultMessage:
                  'As your trusted health partner, we want to ensure you have the information you need to safeguard your health. Are you sure you want to leave this page?',
              })}
            />
          )}
          <MedicalFacts
            medicalFacts={medicalFacts}
            styles={{
              root: {
                paddingLeft: 0,
                paddingRight: 0,
              },
            }}
          />
        </Stack>
      </Container>
    </Box>
  )
}

CtaBanner.displayName = 'CtaBanner'

export { CtaBanner }
